import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import {
  Button, TextField, Container, Typography, Box, Alert, MenuItem, Select, InputLabel, FormControl, CircularProgress, Stack, Fade
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { ThemeProvider } from '@mui/material/styles';
import { useWallet } from '../context/WalletContext';
import { 
  NETWORKS, 
  MINIMAL_ERC20_ABI, 
  POPULAR_TOKENS 
} from '../utils/contractUtils';
import { themeCustom, GradientPaper, AnimatedButton, HeaderBar } from '../utils/themeUtils';

export default function LockTokens() {
  const {
    provider,
    signer,
    address,
    isConnected,
    contract,
    currentNetwork,
    networkConfig,
    wallets,
    activeWallet,
    switchWallet,
    handleNetworkChange,
  } = useWallet();

  const [tokens, setTokens] = useState([]);
  const [selectedToken, setSelectedToken] = useState(null);
  const [manualTokenAddress, setManualTokenAddress] = useState('');
  const [amount, setAmount] = useState("");
  const [lockDuration, setLockDuration] = useState(0);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ type: 'info', message: '' });
  const [showAlert, setShowAlert] = useState(false);
  const [fetchingTokens, setFetchingTokens] = useState(false);
  const [manualTokenInfo, setManualTokenInfo] = useState(null);
  const [approvalPending, setApprovalPending] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Fetch user tokens
  const fetchUserTokens = async (address, baseUrl) => {
    if (!address || !provider) return;
    
    console.log('Fetching tokens for:', address);
    console.log('Network:', currentNetwork);
    console.log('Base URL:', baseUrl);
    
    setFetchingTokens(true);
    let fetchedTokens = [];
    
    try {
      if (currentNetwork === 'bsc') {
        // BSC Network - Uses BSCscan API
        const url = `https://api.bscscan.com/api?module=account&action=tokentx&address=${address}&startblock=0&endblock=99999999&sort=desc&apikey=7Z72QRR7KR58VKTHE3H44DIDC14EBUV91V`;
        console.log('Fetching BSC tokens from:', url);
        
        const response = await fetch(url);
        const data = await response.json();
        
        console.log('BSC API response:', data);
        
        if (data.status === "1" && Array.isArray(data.result)) {
          // Get unique token contracts from transactions
          const uniqueTokens = {};
          data.result.forEach(tx => {
            if (!uniqueTokens[tx.contractAddress]) {
              uniqueTokens[tx.contractAddress] = {
                address: tx.contractAddress,
                symbol: tx.tokenSymbol,
                name: tx.tokenName,
                decimals: parseInt(tx.tokenDecimal),
                balance: "0"
              };
            }
          });
          
          console.log('Unique tokens found:', Object.keys(uniqueTokens).length);
          
          // Fetch balance for each token
          const tokensWithBalances = await Promise.all(
            Object.values(uniqueTokens).map(async (token) => {
              try {
                const tokenContract = new ethers.Contract(token.address, MINIMAL_ERC20_ABI, provider);
                const bal = await tokenContract.balanceOf(address);
                return { ...token, balance: bal.toString() };
              } catch (error) {
                console.error(`Error fetching balance for token ${token.address}:`, error);
                return null;
              }
            })
          );
          
          // Filter out tokens with zero balance
          fetchedTokens = tokensWithBalances.filter(t => t && t.balance !== "0");
          console.log('Tokens with balance:', fetchedTokens.length);
        }
      } else {
        // Ethereum Network - Uses Alchemy API
        console.log('Fetching Ethereum tokens from Alchemy...');
        
        const response = await fetch(baseUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jsonrpc: '2.0',
            id: 1,
            method: 'alchemy_getTokenBalances',
            params: [address],
          }),
        });
        
        if (!response.ok) {
          throw new Error(`Alchemy API request failed: ${response.status} ${response.statusText}`);
        }
        
        const data = await response.json();
        console.log('Alchemy API response:', data);
        
        if (data?.result?.tokenBalances) {
          const allTokens = data.result.tokenBalances;
          console.log('All tokens from Alchemy:', allTokens.length);
          
          // Fetch token metadata for each token
          const tokensWithMeta = await Promise.all(
            allTokens.map(async (t) => {
              try {
                const metaRes = await fetch(baseUrl, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    jsonrpc: '2.0',
                    id: 1,
                    method: 'alchemy_getTokenMetadata',
                    params: [t.contractAddress],
                  }),
                });
                
                if (!metaRes.ok) {
                  console.error(`Metadata request failed for ${t.contractAddress}: ${metaRes.status}`);
                  return null;
                }
                
                const meta = await metaRes.json();
                if (!meta?.result?.symbol) {
                  console.log(`No symbol found for token ${t.contractAddress}`);
                  return null;
                }
                
                return {
                  address: t.contractAddress,
                  balance: t.tokenBalance,
                  symbol: meta.result.symbol,
                  name: meta.result.name,
                  decimals: meta.result.decimals,
                  logo: meta.result.logo,
                };
              } catch (error) {
                console.error(`Error fetching metadata for token ${t.contractAddress}:`, error);
                return null;
              }
            })
          );
          fetchedTokens = tokensWithMeta.filter(token => token !== null);
          console.log('Tokens with metadata:', fetchedTokens.length);
        } else {
          console.log('No token balances found in Alchemy response');
        }
      }
      
      console.log('Final fetched tokens:', fetchedTokens);
      
    } catch (error) {
      console.error('Failed to fetch tokens:', error);
      setAlert({ 
        type: 'error', 
        message: `Failed to fetch tokens for ${networkConfig.name}: ${error.message}` 
      });
      setShowAlert(true);
    } finally {
      setTokens(fetchedTokens);
      setFetchingTokens(false);
    }
  };

  // Fetch tokens when connected
  useEffect(() => {
    if (isConnected && address) {
      fetchUserTokens(address, networkConfig.alchemyBaseUrl);
    }
  }, [isConnected, address, currentNetwork]);

  // Clear state when account or network changes
  useEffect(() => {
    setTokens([]);
    setSelectedToken(null);
    setManualTokenAddress('');
    setAmount('');
    setLockDuration(0);
  }, [address, currentNetwork]);

  // Fetch token info and balance when manualTokenAddress changes
  useEffect(() => {
    const fetchManualTokenInfo = async () => {
      if (!manualTokenAddress || !ethers.utils.isAddress(manualTokenAddress) || !address || !signer) {
        setManualTokenInfo(null);
        return;
      }

      try {
        const tokenContract = new ethers.Contract(manualTokenAddress, MINIMAL_ERC20_ABI, signer);
        const [symbol, name, decimals, balance] = await Promise.all([
        tokenContract.symbol(),
          tokenContract.name(),
        tokenContract.decimals(),
          tokenContract.balanceOf(address)
        ]);

        const formattedBalance = ethers.utils.formatUnits(balance, decimals);

        setManualTokenInfo({
          address: manualTokenAddress,
          symbol,
          name,
          decimals,
          balance: balance.toString(),
          formattedBalance
        });

        setSelectedToken({
          address: manualTokenAddress,
        symbol,
          name,
        decimals,
          balance: balance.toString(),
          formattedBalance
      });

    } catch (err) {
        console.error('Error fetching manual token info:', err);
        setManualTokenInfo(null);
        setSelectedToken(null);
      }
    };

    const timeoutId = setTimeout(() => {
      if (manualTokenAddress && ethers.utils.isAddress(manualTokenAddress)) {
        fetchManualTokenInfo();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [manualTokenAddress, address, signer]);

  // Payment requirement removed - users can now lock tokens directly

  // Alert animation
  useEffect(() => {
    if (alert.message) setShowAlert(true);
    if (alert.message && alert.type === 'success') {
      const timer = setTimeout(() => setShowAlert(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [alert]);

  // Check network function
  const checkNetwork = async () => {
    if (!provider) return false;
    
    try {
      const network = await provider.getNetwork();
      const chainId = network.chainId;
      
      const isSupported = Object.values(NETWORKS).some(n => n.chainId === chainId);
      
      if (!isSupported) {
        setError(`Please switch to ${NETWORKS[currentNetwork].name} to continue.`);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Network check error:', error);
      setError('Failed to check network. Please try again.');
      return false;
    }
  };

  // Payment function removed - users can now lock tokens directly

  // Approve tokens function
  const approveTokens = async (tokenAddress, amount) => {
    try {
      const tokenContract = new ethers.Contract(tokenAddress, MINIMAL_ERC20_ABI, signer);
      const approveTx = await tokenContract.approve(contract.address, amount);
      await approveTx.wait();
      return true;
    } catch (error) {
      console.error('Error approving tokens:', error);
      throw new Error('Failed to approve token transfer. Please try again.');
    }
  };

  // Lock tokens function
  const lockTokens = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Starting lockTokens function...');
      console.log('Contract:', contract);
      console.log('Signer:', signer);
      console.log('Address:', address);

      let tokenDecimals;
      if (selectedToken) {
        tokenDecimals = selectedToken.decimals;
      } else if (manualTokenInfo) {
        tokenDecimals = manualTokenInfo.decimals;
      } else {
        throw new Error('Token information not found');
      }

      const amountInWei = ethers.utils.parseUnits(amount.toString(), tokenDecimals);
      const tokenAddress = selectedToken ? selectedToken.address : manualTokenAddress;

      console.log('Token address:', tokenAddress);
      console.log('Amount in Wei:', amountInWei.toString());
      console.log('Token decimals:', tokenDecimals);

      const isCorrectNetwork = await checkNetwork();
      if (!isCorrectNetwork) {
        return;
      }

      console.log('Network check passed, checking allowance...');

      const tokenContract = new ethers.Contract(tokenAddress, MINIMAL_ERC20_ABI, signer);
      const currentAllowance = await tokenContract.allowance(address, contract.address);
      
      console.log('Current allowance:', currentAllowance.toString());
      console.log('Required amount:', amountInWei.toString());
      
      if (currentAllowance.lt(amountInWei)) {
        setSuccess('Please approve token transfer in your wallet...');
        await approveTokens(tokenAddress, amountInWei);
        setSuccess('Token approval successful! Proceeding with lock...');
      }

      const unlockTime = Math.floor(Date.now() / 1000) + lockDuration;

      console.log('Lock duration:', lockDuration);
      console.log('Unlock time:', unlockTime);
      console.log('Calling contract.lockTokens...');

      const lockTx = await contract.lockTokens(
        tokenAddress,
        amountInWei,
        unlockTime
      );
      
      console.log('Transaction sent:', lockTx.hash);
      console.log('Waiting for confirmation...');
      
      await lockTx.wait();
      console.log('Transaction confirmed!');
      
      setSuccess('Tokens locked successfully! You can unlock them when the time comes.');
      setSelectedToken(null);
      setAmount('');
      setLockDuration(0);

    } catch (error) {
      console.error('Error in lockTokens:', error);
      if (error.message.includes('user rejected')) {
        setError('Transaction was rejected. Please try again.');
      } else {
        setError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Render manual token info
  const renderManualTokenInfo = () => {
    if (!manualTokenInfo) return null;
    return (
      <Box mb={2}>
        <Typography variant="body2" color="textSecondary">
          Token: {manualTokenInfo.symbol} ({manualTokenInfo.name})
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Balance: {manualTokenInfo.formattedBalance}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Address: {manualTokenInfo.address}
        </Typography>
      </Box>
    );
  };

  // Render wallet selector
  const renderWalletSelector = () => {
    if (wallets.length <= 1) return null;

  return (
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="wallet-select-label">Select Wallet</InputLabel>
        <Select
          labelId="wallet-select-label"
          value={activeWallet || ''}
          label="Select Wallet"
          onChange={(e) => switchWallet(e.target.value)}
        >
          {wallets.map((wallet) => (
            <MenuItem key={wallet.address} value={wallet.address}>
              {wallet.address.slice(0, 6)}...{wallet.address.slice(-4)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  // Render lock form
  const renderLockForm = () => {
    return (
      <GradientPaper>
        <Typography 
          variant={isMobile ? 'body1' : 'h6'} 
          gutterBottom 
          sx={{ 
            mb: isMobile ? 2 : 3,
            color: '#0071EB',
            fontWeight: 700
          }}
        >
          Lock Your Tokens
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Stack spacing={isMobile ? 2 : 3}>
          {/* Token Selection */}
          <FormControl fullWidth>
            <InputLabel id="token-select-label" sx={{ color: '#B3B3B3' }}>Select Token</InputLabel>
            <Select
              labelId="token-select-label"
              value={selectedToken ? selectedToken.address : ''}
              label="Select Token"
              onChange={(e) => {
                const token = tokens.find(t => t.address === e.target.value);
                setSelectedToken(token);
              }}
              disabled={tokens.length === 0}
              sx={{
                borderRadius: 2,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255,255,255,0.2)'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#0071EB'
                }
              }}
            >
              {tokens.map((token) => (
                <MenuItem key={token.address} value={token.address}>
                  {token.symbol} ({token.name}) - Balance: {token.formattedBalance}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Manual Token Address */}
          <Box>
            <Typography variant="body2" color="#B3B3B3" sx={{ mb: 1 }}>
              Or enter token address manually:
            </Typography>
            <TextField
              fullWidth
              label="Token Address"
              value={manualTokenAddress}
              onChange={(e) => setManualTokenAddress(e.target.value)}
              placeholder="0x..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '& fieldset': {
                    borderColor: 'rgba(255,255,255,0.2)'
                  },
                  '&:hover fieldset': {
                    borderColor: '#0071EB'
                  }
                }
              }}
            />
            {renderManualTokenInfo()}
          </Box>

          {/* Amount */}
          <TextField
            fullWidth
            label="Amount"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            InputProps={{
              endAdornment: selectedToken && (
                <Typography variant="body2" color="#B3B3B3">
                  {selectedToken.symbol}
                </Typography>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '& fieldset': {
                  borderColor: 'rgba(255,255,255,0.2)'
                },
                '&:hover fieldset': {
                  borderColor: '#0071EB'
                }
              }
            }}
          />

          {/* Lock Duration */}
          <FormControl fullWidth>
            <InputLabel id="duration-select-label" sx={{ color: '#B3B3B3' }}>Lock Duration</InputLabel>
            <Select
              labelId="duration-select-label"
              value={lockDuration}
              label="Lock Duration"
              onChange={(e) => setLockDuration(e.target.value)}
              sx={{
                borderRadius: 2,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255,255,255,0.2)'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#0071EB'
                }
              }}
            >
              <MenuItem value={24 * 60 * 60}>1 Day</MenuItem>
              <MenuItem value={180 * 24 * 60 * 60}>6 Months</MenuItem>
              <MenuItem value={365 * 24 * 60 * 60}>1 Year</MenuItem>
              <MenuItem value={3 * 365 * 24 * 60 * 60}>3 Years</MenuItem>
              <MenuItem value={5 * 365 * 24 * 60 * 60}>5 Years</MenuItem>
              <MenuItem value={7 * 365 * 24 * 60 * 60}>7 Years</MenuItem>
              <MenuItem value={10 * 365 * 24 * 60 * 60}>10 Years</MenuItem>
              <MenuItem value={15 * 365 * 24 * 60 * 60}>15 Years</MenuItem>
            </Select>
          </FormControl>

          {/* Lock Button */}
          <AnimatedButton
            fullWidth
            variant="contained"
            onClick={lockTokens}
            disabled={loading || (!selectedToken && !manualTokenAddress) || !amount || !lockDuration}
            size={isMobile ? 'large' : 'large'}
            sx={{
              background: 'linear-gradient(135deg, #0071EB 0%, #00A3FF 100%)',
              color: '#FFFFFF',
              fontWeight: 700,
              fontSize: isMobile ? '16px' : '18px',
              py: isMobile ? 1.5 : 2,
              mt: 2,
              '&:hover': {
                background: 'linear-gradient(135deg, #005AA3 0%, #0071EB 100%)',
              },
              '&:disabled': {
                background: 'rgba(128, 128, 128, 0.3)',
              }
            }}
          >
            {loading ? <CircularProgress size={24} /> : 'Lock Tokens'}
          </AnimatedButton>
        </Stack>
      </GradientPaper>
    );
  };

  return (
    <ThemeProvider theme={themeCustom}>
      <Box sx={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(135deg, #000000 0%, #141414 50%, #1a1a2e 100%)',
        py: isMobile ? 2 : 6,
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(0, 113, 235, 0.1) 0%, transparent 50%), 
            radial-gradient(circle at 80% 20%, rgba(0, 86, 179, 0.1) 0%, transparent 50%)
          `,
          pointerEvents: 'none',
        }
      }}>
        <Container maxWidth="lg" sx={{ px: isMobile ? 0.5 : 2, position: 'relative', zIndex: 1 }}>
          <HeaderBar>
            <LockIcon sx={{ 
              fontSize: isMobile ? 32 : 64, 
              mr: isMobile ? 1 : 2,
              filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.3))'
            }} />
            <Typography 
              variant={isMobile ? 'h6' : 'h4'} 
              fontWeight={700} 
              letterSpacing={1} 
              sx={{ 
                color: '#fff', 
                textShadow: '0 2px 8px rgba(0,0,0,0.3)',
                background: 'linear-gradient(90deg, #ffffff 0%, #f5f5f1 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Lock Tokens
            </Typography>
          </HeaderBar>

          <Stack spacing={isMobile ? 2 : 3}>
            {showAlert && alert && (
              <Fade in={showAlert} timeout={400} unmountOnExit>
                <Box mb={2} width="100%">
                  <Alert severity={alert.type || 'info'}>{alert.message}</Alert>
                </Box>
              </Fade>
            )}

            {/* Network Selection */}
            <GradientPaper>
              <Typography 
                variant={isMobile ? 'body1' : 'h6'} 
                gutterBottom 
                sx={{ 
                  mb: isMobile ? 2 : 3,
                  color: '#0071EB',
                  fontWeight: 700
                }}
              >
                Select Network
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="#B3B3B3" gutterBottom>
                  Current Network: {NETWORKS[currentNetwork]?.name || 'Unknown'}
                </Typography>
              </Box>
            </GradientPaper>

            {/* Wallet Connection Status */}
            {!isConnected && (
              <GradientPaper>
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant={isMobile ? 'body1' : 'h6'} gutterBottom sx={{ color: '#0071EB', fontWeight: 700 }}>
                    Connect Your Wallet
                  </Typography>
                  <Typography variant="body2" color="#B3B3B3" sx={{ mb: 3 }}>
                    Please connect your wallet using the button in the navigation bar to lock tokens.
                  </Typography>
                </Box>
              </GradientPaper>
            )}

            {/* Lock Form */}
            {isConnected && renderLockForm()}
          </Stack>
        </Container>
      </Box>
    </ThemeProvider>
  );
}